import { invalidateLogbookSurfaces, queryKeys } from "@healthos/client-data";
import { FLAGS } from "@healthos/feature-flags";

import type { api } from "@healthos/schemas";
import { FadersHorizontal, WarningCircle, X } from "@phosphor-icons/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { communityQueryKeys, seedCommunityDemo } from "../../../api/community";
import { client, jsonOrThrow } from "../../../api/healthos";
import { enableWebPushNotifications } from "../../../hooks/use-attention-notification";
import { reportDatadogFeatureFlag } from "../../../lib/datadog";
import {
  DEFAULT_COHORT_CONFIG,
  readCohortConfig,
  resetCohortConfig,
  subscribeCohortConfig,
  writeCohortConfig,
} from "../../../lib/demo-cohort-config";
import { clearAppLocalStorage } from "../../../lib/reset-local-state";
import { useAmbience } from "../../ambience/ambience-player-provider";

import { announceRouteMilestone } from "../../route-milestone-announcer";
import { useCornerReveal } from "../use-corner-reveal";
import { useTweaksPanelAccess } from "../use-tweaks-panel-access";
import { CoachingDemoSection } from "./demo-coaching-section";
import { DemoConfirmProvider } from "./demo-confirm";
import { CustomMemberSection, CustomOpsSection } from "./demo-control-custom-sections";
import {
  addDemoSignal,
  describeTickOutcome,
  devReset,
  fillAllLensesDemoData,
  fillLensDemoData,
  forceHeartbeatTick,
  generateBrief,
  type LensFillVariables,
  type LifeContext,
  lensFillLabel,
  type PersonaId,
  type ProgressEntry,
  personaMeta,
  type RouteContext,
  readArmPerformance,
  readHeartbeatWhy,
  refetch,
  runDemoHeartbeatStory,
  runDemoMomentCard,
  SCENARIOS,
  type Scenario,
  SIGNAL_PRESETS,
  type SignalPreset,
  seedPersona,
  setDemoDay,
  type WhoamiResponse,
} from "./demo-control-model";
import { HeartbeatReadoutSection } from "./demo-control-readout";
import {
  DemoDaySection,
  DemoScriptSection,
  formatNowShowing,
  ProgressGlyph,
  progressColor,
  ScenarioSection,
  SignalPaletteSection,
} from "./demo-control-sections";

/**
 * Floating dev panel for live demos. The primary path reads like a
 * presenter script; expandable sections keep ad-hoc controls close by.
 * Mounted globally in App.tsx, gated on import.meta.env.DEV.
 *
 * Summon with Cmd+. or by moving the cursor into the bottom-right
 * corner. Esc closes.
 */

export function DemoControlPanel() {
  if (import.meta.env.VITE_HIDE_DEMO_PANEL === "true") return null;
  if (import.meta.env.VITE_E2E_MODE === "true") return null;
  if (import.meta.env.DEV) return <DemoControlPanelInner accessLabel="DEV" />;
  return <DemoControlPanelAccessGate />;
}

function DemoControlPanelAccessGate() {
  const tweaksEnabled = useTweaksPanelAccess();

  // Before the early return, and on the value rather than on "did we render":
  // a member who does not have the panel is the control group Datadog needs to
  // compare against.
  useEffect(() => {
    if (tweaksEnabled === undefined) return;
    reportDatadogFeatureFlag(FLAGS.enableTweaksPanel.key, tweaksEnabled);
  }, [tweaksEnabled]);

  if (tweaksEnabled !== true) return null;
  return <DemoControlPanelInner accessLabel="Tweaks" />;
}

function DemoControlPanelInner({ accessLabel }: { accessLabel: string }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const ambience = useAmbience();
  const routeContext: RouteContext = location.pathname.startsWith("/operations")
    ? "ops"
    : "patient";
  const [collapsed, setCollapsed] = useState(true);
  const [activeScenarioId, setActiveScenarioId] = useState<string | null>(null);
  const [progress, setProgress] = useState<ProgressEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<string | null>(null);
  const [iconRevealed, setIconRevealed] = useCornerReveal(collapsed);
  // How many days of history the "Lens data" buttons backfill. 14 so a fill
  // actually reaches Logbook's own 14d/28d detail-range views instead of
  // leaving most of the window empty.
  const [fillDays, setFillDays] = useState<api.DevFillDays>(14);

  const { data: whoamiData } = useQuery({
    queryKey: queryKeys.devWhoami,
    queryFn: async () =>
      jsonOrThrow<WhoamiResponse>(await client().api.me.whoami.$get(), "GET /api/me/whoami"),
    staleTime: 30_000,
    enabled: !collapsed,
  });

  const {
    data: heartbeatWhyData,
    isFetching: heartbeatWhyFetching,
    refetch: refetchHeartbeatWhy,
  } = useQuery({
    queryKey: queryKeys.devHeartbeatWhy,
    queryFn: readHeartbeatWhy,
    staleTime: 5_000,
    enabled: !collapsed,
  });
  const { data: armPerformance, refetch: refetchArmPerformance } = useQuery({
    queryKey: queryKeys.devArmPerformance,
    queryFn: readArmPerformance,
    staleTime: 5_000,
    enabled: !collapsed,
  });

  const [cohortDraft, setCohortDraft] = useState(() => readCohortConfig());
  useEffect(() => subscribeCohortConfig(setCohortDraft), []);
  const cohortChanged =
    cohortDraft.headcount !== readCohortConfig().headcount ||
    cohortDraft.pmpm !== readCohortConfig().pmpm;

  /*
   * A filter over the whole panel rather than one list.
   *
   * The panel has grown past a screenful and the thing you want during a demo
   * is three sections down inside a closed <details>. Rather than teach every
   * section to filter itself, this walks the rendered controls: a control whose
   * text does not match is hidden, a section with no surviving control is
   * hidden, and a section that still has one is forced open so the match is
   * visible without a click. Clearing the box restores everything, including
   * whichever sections the user had opened by hand.
   */
  const [filter, setFilter] = useState("");
  const bodyRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // Closing unmounts the body and reopening mounts a fresh DOM, so this has
    // to run again on reopen or the box shows a filter that is not applied.
    // Reading `collapsed` is what makes that a real dependency rather than one
    // the linter can talk you out of.
    if (collapsed) return;
    const root = bodyRef.current;
    if (!root) return;
    const q = filter.trim().toLowerCase();
    const sections = [...root.querySelectorAll("details")];
    const controls = [...root.querySelectorAll<HTMLElement>("button, a[href]")];

    if (!q) {
      for (const el of controls) el.style.removeProperty("display");
      for (const d of sections) {
        d.style.removeProperty("display");
        if (d.dataset.filterOpened === "1") {
          d.open = false;
          delete d.dataset.filterOpened;
        }
      }
      return;
    }

    for (const el of controls) {
      // The summary toggle is the section's own header, not a control inside
      // it; hiding it would leave a section that cannot be opened.
      if (el.closest("summary")) continue;
      const hit = (el.textContent ?? "").toLowerCase().includes(q);
      el.style.display = hit ? "" : "none";
    }
    for (const d of sections) {
      const survives = [...d.querySelectorAll<HTMLElement>("button, a[href]")].some(
        (el) => !el.closest("summary") && el.style.display !== "none",
      );
      d.style.display = survives ? "" : "none";
      if (survives && !d.open) {
        d.open = true;
        d.dataset.filterOpened = "1";
      }
    }
  }, [filter, collapsed]);

  // Cmd+. summons; Esc closes.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === ".") {
        e.preventDefault();
        setCollapsed((c) => !c);
        // Keep the icon visible right after closing, cursor is likely still in the corner.
        if (!collapsed) setIconRevealed(true);
      } else if (e.key === "Escape" && !collapsed) {
        setCollapsed(true);
        setIconRevealed(true);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [collapsed, setIconRevealed]);

  const runScenario = async (s: Scenario) => {
    if (activeScenarioId !== null) return;
    setActiveScenarioId(s.id);
    setError(null);
    setLastResult(null);
    try {
      await s.run({ qc, navigate, report: setProgress, userId: whoamiData?.userId ?? null });
      await refetch(qc, queryKeys.devWhoami, queryKeys.devHeartbeatWhy);
      setLastResult(s.label);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setProgress((p) =>
        p.map((entry) => (entry.status === "running" ? { ...entry, status: "error" } : entry)),
      );
    }
    setTimeout(() => setActiveScenarioId(null), 1500);
  };

  const seedMutation = useMutation({
    mutationFn: ({ persona, lifeContext }: { persona: PersonaId; lifeContext: LifeContext }) =>
      seedPersona(persona, lifeContext),
    onSuccess: async (_data, vars) => {
      await refetch(qc, queryKeys.ambient, queryKeys.score, queryKeys.devWhoami);
      await qc.invalidateQueries({ queryKey: queryKeys.devHeartbeatWhy, refetchType: "active" });
      setLastResult(
        `Seeded ${personaMeta(vars.persona).short}${vars.lifeContext === "low_agency" ? " (survival)" : ""}`,
      );
    },
    onError: (e) => setError(e instanceof Error ? e.message : String(e)),
  });

  const resetMutation = useMutation({
    mutationFn: devReset,
    onSuccess: () => {
      // Reset bypasses the boot wipe that normally fades the ambience, so stop
      // it here or the onboarding loop keeps playing into the reset. Rewind as
      // well: fadeOut pauses where it stands, so the next play resumes
      // mid-track and the old playhead is written back on the next pagehide.
      ambience.fadeOut();
      ambience.rewind();
      // Credentials too, then a full load rather than a client-side navigate:
      // oidc-client-ts keeps the user in memory, so an SPA hop would leave the
      // session alive and the reset half-done.
      clearAppLocalStorage({ includeAuth: true });
      qc.clear();
      setLastResult("Reset to onboarding");
      window.location.assign("/");
    },
    onError: (e) => setError(e instanceof Error ? e.message : String(e)),
  });

  // Replays Day Zero for the signed-in member: clears the orientation and
  // comprehension dismissals plus the recorded answer, so the run starts over
  // without touching their plan, signals or completions.
  const dayZeroResetMutation = useMutation({
    mutationFn: () =>
      client()
        .api["day-zero"].reset.$post()
        .then((r) => jsonOrThrow<api.DayZeroResetResponse>(r, "POST /api/day-zero/reset")),
    onSuccess: async () => {
      await refetch(qc, queryKeys.ambient, queryKeys.explainerDismissals, ["day-zero"]);
      setLastResult("Day Zero replayed");
    },
    onError: (e) => setError(e instanceof Error ? e.message : String(e)),
  });

  const backfillMutation = useMutation({
    mutationFn: () =>
      client()
        .api.me["demo-seed"].$post()
        .then((r) => jsonOrThrow(r, "POST /api/me/demo-seed")),
    onSuccess: async () => {
      await refetch(qc, queryKeys.ambient, queryKeys.score, ["operations"]);
      setLastResult("Backfilled 7 days of signals");
    },
    onError: (e) => setError(e instanceof Error ? e.message : String(e)),
  });

  const lensFillMutation = useMutation({
    mutationFn: ({ lens, days }: LensFillVariables) => fillLensDemoData(lens, days),
    onSuccess: async (data) => {
      // Lens fill writes nutrition.meal/hydration.ml/connection.log/
      // wearable.weight — all Logbook-pillar kinds — but none of the keys
      // below touch Logbook's own queries, so without this the chart/
      // history kept serving stale (often empty) cached data after a fill.
      invalidateLogbookSurfaces(qc);
      await refetch(
        qc,
        queryKeys.ambient,
        queryKeys.score,
        queryKeys.glp1Tracking,
        queryKeys.devWhoami,
        queryKeys.devHeartbeatWhy,
      );
      const trackingCopy = data.tracking
        ? `, ${data.tracking.doses} doses, ${data.tracking.weights} weights`
        : "";
      setLastResult(
        `Filled ${lensFillLabel(data.lens)} (${data.days}d): ${data.signals} signals${trackingCopy}`,
      );
    },
    onError: (e) => setError(e instanceof Error ? e.message : String(e)),
  });

  const fillAllLensesMutation = useMutation({
    mutationFn: (days: api.DevFillDays) => fillAllLensesDemoData(days),
    onSuccess: async (data) => {
      // Same gap as lensFillMutation above — this writes every lens's
      // Logbook-pillar kinds at once, so the invalidation matters even more.
      invalidateLogbookSurfaces(qc);
      await refetch(
        qc,
        queryKeys.ambient,
        queryKeys.score,
        queryKeys.devWhoami,
        queryKeys.devHeartbeatWhy,
      );
      setLastResult(`Filled all lenses (${data.days}d): ${data.signals} signals`);
    },
    onError: (e) => setError(e instanceof Error ? e.message : String(e)),
  });

  // `force` runs it as an operator: past the cooldown and the quiet gates.
  const tickMutation = useMutation({
    mutationFn: (force: boolean) => forceHeartbeatTick(whoamiData?.userId, force),
    onSuccess: async (response) => {
      await refetch(
        qc,
        queryKeys.ambient,
        queryKeys.score,
        queryKeys.devWhoami,
        queryKeys.devHeartbeatWhy,
      );
      setLastResult(describeTickOutcome(response));
    },
    onError: (e) => setError(e instanceof Error ? e.message : String(e)),
  });

  const demoDayMutation = useMutation({
    mutationFn: setDemoDay,
    onSuccess: async (result, day) => {
      // Same gap as lensFillMutation above: the glp1_companion persona's
      // day-progression signals include nutrition.meal/hydration.ml, which
      // Logbook's own query keys below never covered.
      invalidateLogbookSurfaces(qc);
      await refetch(qc, queryKeys.ambient, queryKeys.score, queryKeys.devWhoami);
      const briefNote = result.brief.status === "skipped" ? "" : `, brief ${result.brief.status}`;
      setLastResult(`D${day} ready: ${result.signals} signals${briefNote}`);
    },
    onError: (e) => setError(e instanceof Error ? e.message : String(e)),
  });

  const briefMutation = useMutation({
    mutationFn: () => {
      const userId = whoamiData?.userId;
      if (!userId) throw new Error("No user loaded");
      return generateBrief(userId);
    },
    onSuccess: async () => {
      await refetch(qc, queryKeys.ambient, queryKeys.score);
      setLastResult("Brief generated");
    },
    onError: (e) => setError(e instanceof Error ? e.message : String(e)),
  });

  const demoStoryMutation = useMutation({
    mutationFn: runDemoHeartbeatStory,
    onSuccess: async (data, story) => {
      await refetch(
        qc,
        queryKeys.ambient,
        queryKeys.score,
        queryKeys.devWhoami,
        queryKeys.devHeartbeatWhy,
      );
      if (story !== "quiet_log") navigate("/");

      setLastResult(`${data.surface.title} staged`);
    },
    onError: (e) => setError(e instanceof Error ? e.message : String(e)),
  });

  // The capture gallery forces a specific "log this now" prompt onto /today,
  // bypassing eligibility so each prompt's card + tap-to-log flow is one click.
  const ruleMutation = useMutation({
    mutationFn: runDemoMomentCard,
    onSuccess: async (data) => {
      await refetch(qc, queryKeys.ambient, queryKeys.score, queryKeys.devWhoami);
      navigate("/");
      setLastResult(`Rung ${data.rung} staged`);
    },
    onError: (e) => setError(e instanceof Error ? e.message : String(e)),
  });

  const testPushMutation = useMutation({
    mutationFn: async () => {
      // The server fans out to OneSignal (phone) and web push; the browser
      // only receives it once permission is granted and a subscription
      // exists, so establish that first instead of failing silently.
      const { status } = await enableWebPushNotifications();
      if (status !== "subscribed") {
        throw new Error(`Browser push unavailable: ${status.replaceAll("_", " ")}`);
      }
      return jsonOrThrow(await client().api.me["dev-push"].$post(), "POST /api/me/dev-push");
    },
    onSuccess: () => setLastResult("Test push sent (browser + phone)"),
    onError: (e) => setError(e instanceof Error ? e.message : String(e)),
  });

  const allPushTypesMutation = useMutation({
    mutationFn: async () =>
      jsonOrThrow<api.DevPushAllResponse>(
        await client().api.me["dev-push-all"].$post(),
        "POST /api/me/dev-push-all",
      ),
    onSuccess: (result) => {
      setLastResult(
        result.failed === 0
          ? `${result.sent} notification types sent`
          : `${result.sent}/${result.total} notification types sent; ${result.failed} failed`,
      );
    },
    onError: (e) => setError(e instanceof Error ? e.message : String(e)),
  });

  /*
   * Forces a surface from the member's own signals. The other forced path
   * injects a synthetic short-sleep reading to get there, which is fine for the
   * "Force attention" scenario that says so in its hint, and wrong for a button
   * whose job is to show what heartbeat would do with the data they actually have.
   */
  const forceAttentionMutation = useMutation({
    mutationFn: () => forceHeartbeatTick(whoamiData?.userId, true),
    onSuccess: async (response) => {
      // Bypassing every gate does not guarantee a card: the agent can still
      // pick an action that surfaces nothing, and this used to claim one either way.
      setLastResult(describeTickOutcome(response));
      await refetch(qc, queryKeys.ambient, queryKeys.score);
    },
    onError: (e) => setError(e instanceof Error ? e.message : String(e)),
  });

  const communitySeedMutation = useMutation({
    mutationFn: seedCommunityDemo,
    onSuccess: async (data) => {
      await refetch(
        qc,
        communityQueryKeys.feed,
        communityQueryKeys.adminQueues,
        communityQueryKeys.adminSettings(),
      );
      const skipped = data.seeded.skippedImages
        ? `, ${data.seeded.skippedImages} images skipped`
        : "";
      const noRecipe = data.seeded.recipeUnresolved
        ? ", no recipe card (no recipe indexed, reindex content)"
        : "";
      setLastResult(
        `Community seeded: ${data.seeded.coworkers} coworkers, ${data.seeded.moments} moments, ${data.seeded.kudos} kudos, ${data.seeded.images} images${skipped}${noRecipe}`,
      );
    },
    onError: (e) => setError(e instanceof Error ? e.message : String(e)),
  });

  const signalMutation = useMutation({
    mutationFn: (preset: SignalPreset) =>
      addDemoSignal(preset, whoamiData?.demoExperienceDay ?? null),
    onSuccess: async (_data, preset) => {
      await refetch(qc, queryKeys.ambient, queryKeys.score);
      const meta = SIGNAL_PRESETS.find((p) => p.id === preset);
      setLastResult(`Added ${meta?.label ?? preset}. Tick when ready`);
    },
    onError: (e) => setError(e instanceof Error ? e.message : String(e)),
  });

  const routeWaypointMutation = useMutation({
    mutationFn: async () =>
      jsonOrThrow<api.DevRouteWaypointResponse>(
        await client().api.me["dev-route-waypoint"].$post(),
        "POST /api/me/dev-route-waypoint",
      ),
    onSuccess: (result) => {
      // A real celebration for the member's own route, so the share button
      // publishes to the community for real.
      announceRouteMilestone(result.celebration);
      setLastResult(`Replayed ${result.celebration.milestoneTitle}`);
    },
    onError: (e) => setError(e instanceof Error ? e.message : String(e)),
  });

  const journeyProgressMutation = useMutation({
    mutationFn: async (input: api.DevJourneyProgressRequest) =>
      jsonOrThrow<api.DevJourneyProgressResponse>(
        await client().api.me["dev-journey-progress"].$post({ json: input }),
        "POST /api/me/dev-journey-progress",
      ),
    onSuccess: async (result) => {
      await refetch(qc, ["challenges"]);
      setLastResult(
        result.completed
          ? `${result.journeyName} completed`
          : `Added ${result.addedSteps.toLocaleString()} journey steps`,
      );
    },
    onError: (e) => setError(e instanceof Error ? e.message : String(e)),
  });

  const challengesSeedMutation = useMutation({
    mutationFn: async () =>
      jsonOrThrow<api.DevSeedChallengesResponse>(
        await client().api.me["dev-seed-challenges"].$post(),
        "POST /api/me/dev-seed-challenges",
      ),
    onSuccess: async (result) => {
      await refetch(qc, ["challenges"], ["walking-groups"], ["activities"]);
      setLastResult(
        `Seeded ${result.teammates} route teammates, ${result.groups} groups, ${result.leaderboards} leaderboards, and ${result.activities} activities`,
      );
    },
    onError: (e) => setError(e instanceof Error ? e.message : String(e)),
  });

  const pastChallengesSeedMutation = useMutation({
    mutationFn: async () =>
      jsonOrThrow<api.DevSeedPastChallengesResponse>(
        await client().api.me["dev-seed-past-challenges"].$post(),
        "POST /api/me/dev-seed-past-challenges",
      ),
    onSuccess: async (result) => {
      await refetch(qc, ["challenges"]);
      setLastResult(
        result.challenges > 0
          ? `Seeded ${result.challenges} past challenges with ${result.participants} participants`
          : "Company challenges already start on those dates, so nothing was replaced",
      );
    },
    onError: (e) => setError(e instanceof Error ? e.message : String(e)),
  });

  const justEndedSeedMutation = useMutation({
    mutationFn: async () =>
      jsonOrThrow<api.DevSeedPastChallengesResponse>(
        await client().api.me["dev-seed-just-ended-challenge"].$post(),
        "POST /api/me/dev-seed-just-ended-challenge",
      ),
    onSuccess: async (result) => {
      await refetch(qc, ["challenges"]);
      setLastResult(
        result.challenges > 0
          ? `Seeded a challenge that ended three days ago with ${result.participants} participants`
          : "A company challenge already starts on that date, so nothing was replaced",
      );
    },
    onError: (e) => setError(e instanceof Error ? e.message : String(e)),
  });

  const activateRouteVoteMutation = useMutation({
    mutationFn: async () =>
      jsonOrThrow<api.DevActivateCompanyRouteVoteResponse>(
        await client().api.me["dev-activate-route-vote"].$post(),
        "POST /api/me/dev-activate-route-vote",
      ),
    onSuccess: async (result) => {
      await refetch(qc, ["challenges"]);
      setLastResult(result.active ? "Next route voting is active" : "No active company challenge");
    },
    onError: (e) => setError(e instanceof Error ? e.message : String(e)),
  });

  const stepHistoryMutation = useMutation({
    mutationFn: async () =>
      jsonOrThrow<api.DevSeedStepHistoryResponse>(
        await client().api.me["dev-seed-step-history"].$post(),
        "POST /api/me/dev-seed-step-history",
      ),
    onSuccess: async (result) => {
      await refetch(qc, ["steps", "dashboard"], ["challenges"]);
      setLastResult(
        `Seeded ${result.daysSeeded} days of steps and distance with a ${result.streakDays}-day streak`,
      );
    },
    onError: (e) => setError(e instanceof Error ? e.message : String(e)),
  });

  const cohortMutation = useMutation({
    mutationFn: async (vars: { headcount: number; pmpm: number }) => {
      writeCohortConfig(vars);
      await refetch(qc, ["operations"]);
    },
    onSuccess: () => {
      setLastResult(
        `Cohort: ${cohortDraft.headcount.toLocaleString()} lives @ $${cohortDraft.pmpm} PMPM`,
      );
    },
    onError: (e) => setError(e instanceof Error ? e.message : String(e)),
  });

  const isBusy =
    activeScenarioId !== null ||
    seedMutation.isPending ||
    resetMutation.isPending ||
    dayZeroResetMutation.isPending ||
    backfillMutation.isPending ||
    lensFillMutation.isPending ||
    fillAllLensesMutation.isPending ||
    tickMutation.isPending ||
    briefMutation.isPending ||
    demoStoryMutation.isPending ||
    demoDayMutation.isPending ||
    signalMutation.isPending ||
    journeyProgressMutation.isPending ||
    routeWaypointMutation.isPending ||
    challengesSeedMutation.isPending ||
    pastChallengesSeedMutation.isPending ||
    justEndedSeedMutation.isPending ||
    stepHistoryMutation.isPending ||
    testPushMutation.isPending ||
    allPushTypesMutation.isPending ||
    communitySeedMutation.isPending ||
    cohortMutation.isPending;

  if (collapsed) {
    return (
      <button
        type="button"
        onClick={() => setCollapsed(false)}
        aria-label="Open demo control panel (Cmd+.)"
        title="Demo controls (Cmd+.)"
        className={`fixed right-5 bottom-5 z-50 grid h-8 w-8 place-items-center rounded-md bg-navy/15 text-ink-primary/55 backdrop-blur-md backdrop-saturate-150 transition-opacity duration-300 ease-out hover:bg-navy/35 hover:text-ink-primary/90 focus-visible:bg-navy/35 focus-visible:text-ink-primary/90 ${
          iconRevealed ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
      >
        <FadersHorizontal size={14} weight="regular" />
      </button>
    );
  }

  const data = whoamiData;
  const nowShowing = formatNowShowing(routeContext, location.pathname, data, cohortDraft);

  return (
    <aside
      className="motion-safe:animate-modal-scale-in fixed right-5 bottom-5 z-50 flex w-[420px] max-w-[calc(100vw-40px)] flex-col overflow-hidden rounded-2xl border border-surface-elevated/55 bg-surface-elevated/78 shadow-card-hover backdrop-blur-2xl backdrop-saturate-150"
      style={{ transformOrigin: "bottom right", maxHeight: "calc(100vh - 40px)" }}
      aria-label="Demo control panel"
    >
      <DemoConfirmProvider>
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-surface-elevated/80 to-transparent"
        />
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-gradient-to-b from-ink-on-media/30 via-transparent to-transparent"
        />

        <header className="relative z-10 flex shrink-0 items-center justify-between gap-2 p-3.5 pb-2">
          <span className="inline-flex items-center gap-1.5 font-mono text-[9.5px] uppercase tracking-[0.14em] text-ink-tertiary">
            <FadersHorizontal size={10} weight="regular" />
            Demo · {accessLabel}
            <span className="ml-1 rounded bg-surface-track/70 px-1 py-px font-mono text-[8.5px] text-ink-tertiary">
              ⌘.
            </span>
          </span>
          <button
            type="button"
            onClick={() => {
              setCollapsed(true);
              setIconRevealed(true);
            }}
            className="grid h-6 w-6 place-items-center rounded-md text-ink-quaternary transition-colors hover:bg-surface-track/70 hover:text-ink-body"
            aria-label="Close demo panel (Esc)"
          >
            <X size={12} weight="bold" />
          </button>
        </header>

        <div className="relative z-10 shrink-0 px-3.5 pb-2">
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter controls…"
            aria-label="Filter demo controls"
            className="w-full rounded-md border border-surface-elevated/60 bg-surface-elevated/70 px-2.5 py-1.5 font-mono text-xs text-ink-primary placeholder:text-ink-quaternary focus:border-teal-500/50 focus:outline-none"
          />
        </div>

        <div
          ref={bodyRef}
          className="relative flex min-h-0 flex-col gap-3 overflow-y-auto px-3.5 pb-3.5"
        >
          <div className="rounded-lg border border-surface-elevated/40 bg-surface-elevated/70 px-3 py-2">
            <p className="font-mono text-[8.5px] uppercase tracking-[0.14em] text-ink-tertiary">
              Now showing
            </p>
            <p className="mt-0.5 font-medium text-[12.5px] leading-snug text-ink-primary">
              {nowShowing}
            </p>
          </div>

          <HeartbeatReadoutSection
            data={data}
            heartbeatWhy={heartbeatWhyData}
            arms={armPerformance}
            loading={heartbeatWhyFetching}
            onRefresh={() => {
              void refetchHeartbeatWhy();
              void refetchArmPerformance();
            }}
          />

          <DemoScriptSection
            data={data}
            isBusy={isBusy}
            seedMutation={seedMutation}
            resetMutation={resetMutation}
            dayZeroResetMutation={dayZeroResetMutation}
            demoDayMutation={demoDayMutation}
            demoStoryMutation={demoStoryMutation}
            lensFillMutation={lensFillMutation}
            signalMutation={signalMutation}
            ruleMutation={ruleMutation}
          />

          <DemoDaySection
            data={data}
            isBusy={isBusy}
            routeContext={routeContext}
            demoDayMutation={demoDayMutation}
            tickMutation={tickMutation}
            briefMutation={briefMutation}
            testPushMutation={testPushMutation}
            allPushTypesMutation={allPushTypesMutation}
            forceAttentionMutation={forceAttentionMutation}
          />

          <SignalPaletteSection isBusy={isBusy} signalMutation={signalMutation} />

          <ScenarioSection
            scenarios={SCENARIOS}
            isBusy={isBusy}
            activeScenarioId={activeScenarioId}
            routeContext={routeContext}
            onRun={runScenario}
          />

          {progress.length > 0 ? (
            <div className="flex flex-col gap-1 rounded-lg border border-teal-500/30 bg-surface-secondary/40 px-3 py-2">
              {progress.map((entry, i) => (
                <div
                  // biome-ignore lint/suspicious/noArrayIndexKey: append-only demo progress log; entries never reorder, labels can repeat (retries), index plus label is the stable identity.
                  key={`${entry.label}-${i}`}
                  className="flex items-center gap-2 font-mono text-[10.5px]"
                >
                  <ProgressGlyph status={entry.status} />
                  <span className={progressColor(entry.status)}>{entry.label}</span>
                </div>
              ))}
            </div>
          ) : null}

          <CustomMemberSection
            data={data}
            isBusy={isBusy}
            routeContext={routeContext}
            seedMutation={seedMutation}
            backfillMutation={backfillMutation}
            lensFillMutation={lensFillMutation}
            fillDays={fillDays}
            onFillDaysChange={setFillDays}
            fillAllLensesMutation={fillAllLensesMutation}
            communitySeedMutation={communitySeedMutation}
            tickMutation={tickMutation}
            resetMutation={resetMutation}
            routeWaypointMutation={routeWaypointMutation}
            journeyProgressMutation={journeyProgressMutation}
            challengesSeedMutation={challengesSeedMutation}
            pastChallengesSeedMutation={pastChallengesSeedMutation}
            justEndedSeedMutation={justEndedSeedMutation}
            activateRouteVoteMutation={activateRouteVoteMutation}
            stepHistoryMutation={stepHistoryMutation}
          />
          <CoachingDemoSection
            isBusy={isBusy}
            onResult={(message) => {
              setError(null);
              setLastResult(message);
            }}
            onError={setError}
          />
          <CustomOpsSection
            isBusy={isBusy}
            routeContext={routeContext}
            cohortDraft={cohortDraft}
            cohortChanged={cohortChanged}
            setCohortDraft={setCohortDraft}
            cohortMutation={cohortMutation}
            onResetCohort={() => {
              setCohortDraft(DEFAULT_COHORT_CONFIG);
              resetCohortConfig();
              refetch(qc, ["operations"]);
              setLastResult("Cohort reset to defaults");
            }}
          />

          {lastResult && !error ? (
            <div className="rounded-md bg-surface-accent-leaf/70 px-2.5 py-1.5 text-[10.5px] text-ink-accent-leaf">
              ✓ {lastResult}
            </div>
          ) : null}
          {error ? (
            <div className="flex items-start gap-1.5 rounded-md bg-rose-50/80 px-2.5 py-1.5 text-[10.5px] text-rose-800">
              <WarningCircle size={12} weight="bold" className="mt-0.5 shrink-0" />
              <span className="break-all">{error}</span>
            </div>
          ) : null}
        </div>
      </DemoConfirmProvider>
    </aside>
  );
}

/* ─── Custom controls subsections ─────────────────────────────── */
